import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Box, Typography, Button, Autocomplete, TextField } from '@mui/material';
import './CustomGrid.css';
import CustomerAutocomplete from './CustomerAutocomplete.js';
import FieldAutocomplete from './FieldAutocomplete.js';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';

// 한글 입력을 위한 커스텀 편집 셀 컴포넌트
const CustomEditCell = (props) => {
    const { id, field, value, api } = props;
    const [localValue, setLocalValue] = useState(value || '');
    const [isComposing, setIsComposing] = useState(false);
    const inputRef = React.useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, []);

    const handleCompositionStart = () => {
        setIsComposing(true);
    };

    const handleCompositionEnd = (event) => {
        setIsComposing(false);
        setLocalValue(event.target.value);
    };

    const handleChange = (event) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
        // 실시간으로 값 업데이트 (조합 중이 아닐 때만)
        if (!isComposing) {
            api.setEditCellValue({ id, field, value: newValue }, event);
        }
    };

    const commitAndStop = () => {
        api.setEditCellValue({ id, field, value: localValue });
        api.stopCellEditMode({ id, field });
    };

    const handleBlur = () => {
        commitAndStop();
    };

    const handleKeyDown = (event) => {
        // 한글 조합 중에는 Enter/Tab 무시
        if (isComposing) return;

        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            // Tab의 경우 값 커밋 후 다음 셀로 이동
            api.setEditCellValue({ id, field, value: localValue });
            api.stopCellEditMode({ id, field });

            if (event.key === 'Tab') {
                // Tab 이벤트를 그리드에 전달하여 다음 셀로 이동
                event.defaultMuiPrevented = false;
            }
        } else if (event.key === 'Escape') {
            api.stopCellEditMode({ id, field, ignoreModifications: true });
        }
    };

    return (
        <TextField
            inputRef={inputRef}
            value={localValue}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            fullWidth
            variant="standard"
            InputProps={{
                disableUnderline: true,
                style: {
                    fontSize: 'inherit',
                    padding: '0 8px'
                }
            }}
        />
    );
};

// 공통 그리드 컴포넌트
export default function CustomGrid({
    dataRows,
    dataColumns,
    title,
    addRowBtn,
    onAddRow,
    onSave,
    saveBtn,
    deleteBtn,
    onDelete,
    onFieldSearch,
    onFieldIdChange,
    onCustomerSearch,
    onYearMonthSearch,
    onCustomerIdChange,
    onYearMonthChange,
    onSearch,
    searchDate,
    ...rest
}) {
    const rows = dataRows || [];

    const columns = useMemo(() => {
        return (dataColumns || []).map(col => {
            if (col.editable && col.type !== 'number' && !col.renderEditCell) {
                return {
                    ...col,
                    renderEditCell: (params) => <CustomEditCell {...params} />
                };
            }
            return col;
        });
    }, [dataColumns]);

    return (
        <div>
            <h2>{title}</h2>
            <div className="gh-band">
                <div className="gh-band__row">
                    <div className="gh-band__title">
                        <span className="gh-octo" aria-hidden>▶</span>
                        <span>조회조건</span>
                        <span className="gh-badge">조회결과: {dataRows.length}</span>
                    </div>
                </div>
                <div className="gh-band__filters">
                    {(onYearMonthSearch) && (
                        <div className="filter-item gh-field">
                            <label htmlFor="monthPicker">조회 년월</label>
                            <DatePicker
                                id="monthPicker"
                                selected={searchDate}
                                onChange={onYearMonthChange}
                                onYearMonthSelect={onYearMonthSearch}
                                dateFormat="yyyy-MM"
                                showMonthYearPicker
                                placeholderText="년월 선택"
                                locale={ko}
                                className="gh-input"
                                portalId="portal-root"
                                popperPlacement="bottom-start"
                            />
                        </div>
                    )}
                    {(onFieldSearch) && (
                        <FieldAutocomplete onFieldSelect={onFieldIdChange} />
                    )}
                    {(onCustomerSearch) && (
                        <CustomerAutocomplete onCustomerSelect={onCustomerIdChange} />
                    )}

                    <div className="filter-actions gh-actions">
                        <button className="btn" onClick={onSearch}>조회</button>
                    </div>
                </div>
            </div>
            <p></p>
            <Box sx={{ p: 0, flexGrow: 1 }}>
                {(saveBtn || addRowBtn || deleteBtn) && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: 1,
                            gap: 1
                        }}
                    >
                        {addRowBtn && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onAddRow}
                                size="small"
                            >
                                행 추가
                            </Button>
                        )}
                        {saveBtn && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={onSave}
                                size="small"
                            >
                                저장
                            </Button>
                        )}
                        {deleteBtn && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={onDelete}
                                size="small"
                            >
                                삭제
                            </Button>
                        )}
                    </Box>
                )}

                <div style={{ height: '100%', width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                        getRowId={(row) => row.id}
                        rowHeight={38}
                        headerHeight={40}
                        checkboxSelection
                        disableRowSelectionOnClick
                        editMode="cell"
                        experimentalFeatures={{ newEditingApi: true }}
                        isCellEditable={(params) => true}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        {...rest}
                    />
                </div>
            </Box>
        </div>
    );
}